class InformationEqualizer {
  constructor(section) {
    this.minWidthQuery = window.matchMedia('(min-width: 768px)');
    this.section = section;
    this.rowBodies = Array.from(
      section.querySelectorAll('.information__row-body'),
    );

    if (!this.rowBodies.length) return;

    this.handleResize = this.update.bind(this);
    window.addEventListener('resize', this.handleResize);

    this.update();
  }

  resetHeights() {
    this.rowBodies.forEach((rowBody) => {
      const elements = rowBody.querySelectorAll('.information__element');
      elements.forEach((el) => {
        el.style.minHeight = '';
      });
    });
  }

  update() {
    if (!this.minWidthQuery.matches) {
      this.resetHeights();
      return;
    }

    this.rowBodies.forEach((rowBody) => {
      if (!rowBody.offsetParent) return;

      const col1 = rowBody.querySelector(
        '.information__col--1 .information__col-elements',
      );
      const col2 = rowBody.querySelector(
        '.information__col--2 .information__col-elements',
      );

      if (!col1 || !col2) return;

      const elements1 = Array.from(
        col1.querySelectorAll('.information__element'),
      );
      const elements2 = Array.from(
        col2.querySelectorAll('.information__element'),
      );

      if (!elements1.length || !elements2.length) return;

      [...elements1, ...elements2].forEach((el) => {
        el.style.minHeight = '';
      });

      const maxLen = Math.max(elements1.length, elements2.length);

      for (let i = 0; i < maxLen; i += 1) {
        const el1 = elements1[i];
        const el2 = elements2[i];

        if (!el1 || !el2) continue;

        const maxHeight = Math.max(el1.offsetHeight, el2.offsetHeight);

        if (!maxHeight) continue;

        el1.style.minHeight = `${maxHeight}px`;
        el2.style.minHeight = `${maxHeight}px`;
      }
    });
  }

  destroy() {
    window.removeEventListener('resize', this.handleResize);
    this.resetHeights();
  }
}

class InformationTabs {
  constructor(section, equalizer) {
    this.section  = section;
    this.equalizer = equalizer;
    this.buttons  = Array.from(section.querySelectorAll('.information__tab-btn'));
    this.panels   = Array.from(section.querySelectorAll('.information__tab-panel'));
    this.navInner = section.querySelector('.information__tabs-nav-inner');
    this.arrowPrev = section.querySelector('.information__tabs-arrow--prev');
    this.arrowNext = section.querySelector('.information__tabs-arrow--next');

    if (!this.buttons.length) return;

    this.bindEvents();
    this.initPanelsState();

    if (this.equalizer && typeof this.equalizer.update === 'function') {
      this.equalizer.update();
    }
  }

  bindEvents() {
    this.buttons.forEach((btn) => {
      btn.addEventListener('click', () => this.activate(btn));
    });

    if (this.arrowNext && this.navInner) {
      this.arrowNext.addEventListener('click', () => {
        this.navInner.scrollBy({ left: 240, behavior: 'smooth' });
      });
    }

    if (this.arrowPrev && this.navInner) {
      this.arrowPrev.addEventListener('click', () => {
        this.navInner.scrollBy({ left: -240, behavior: 'smooth' });
      });
    }

    if (this.navInner) {
      this.initDragScroll();
    }
  }

  initPanelsState() {
    const activePanel = this.panels.find((panel) =>
      panel.classList.contains('information__tab-panel--active'),
    );

    if (activePanel) {
      activePanel.classList.add('information__tab-panel--visible');
    }
  }

  initDragScroll() {
    const el        = this.navInner;
    let isDragging  = false;
    let startX      = 0;
    let scrollLeft  = 0;
    let hasMoved    = false;

    el.addEventListener('mousedown', (e) => {
      isDragging = true;
      hasMoved   = false;
      startX     = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
    });

    document.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      const x    = e.pageX - el.offsetLeft;
      const diff = x - startX;

      if (Math.abs(diff) > 5) {
        hasMoved = true;
      }

      el.scrollLeft = scrollLeft - diff;
    });

    document.addEventListener('mouseup', () => {
      if (!isDragging) return;
      isDragging = false;
      el.style.cursor = '';
      el.style.userSelect = '';
    });

    this.buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        if (hasMoved) {
          e.stopImmediatePropagation();
          hasMoved = false;
        }
      }, true);
    });
  }

  activate(activeBtn) {
    const tabIndex = activeBtn.dataset.tab;

    const targetPanel = this.panels.find(
      (panel) => panel.dataset.tabPanel === tabIndex,
    );

    if (!targetPanel) {
      return;
    }

    if (targetPanel.classList.contains('information__tab-panel--active')) {
      return;
    }

    this.buttons.forEach((btn) => {
      const isActive = btn === activeBtn;
      btn.classList.toggle('information__tab-btn--active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
    });

    this.panels.forEach((panel) => {
      const isTarget = panel === targetPanel;

      panel.classList.toggle('information__tab-panel--active', isTarget);

      if (!isTarget) {
        panel.classList.remove('information__tab-panel--visible');
      }
    });

    requestAnimationFrame(() => {
      targetPanel.classList.add('information__tab-panel--visible');

      if (this.equalizer && typeof this.equalizer.update === 'function') {
        this.equalizer.update();
      }
    });

    activeBtn.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }
}

class InformationAccordion {
  constructor(section) {
    this.triggers = Array.from(section.querySelectorAll('.information__accordion-trigger'));

    if (!this.triggers.length) return;

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.toggle(trigger));
    });
  }

  toggle(trigger) {
    const body = trigger.nextElementSibling;
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    trigger.setAttribute('aria-expanded', String(!isOpen));
    body.classList.toggle('is-open', !isOpen);
  }
}

document.querySelectorAll('.information').forEach((section) => {
  const equalizer = new InformationEqualizer(section);

  new InformationTabs(section, equalizer);
  new InformationAccordion(section);
});
